import React, { useEffect, useState } from 'react';
import { BarChart3Icon, MousePointerClickIcon, CheckCircle2Icon, TrendingUpIcon, RefreshCwIcon, TrashIcon } from 'lucide-react';
import { calculateCTR, getAnalyticsEvents, clearAnalytics, type CTRMetrics } from '../utils/analytics';
type TimePeriod = 'today' | '7days' | '30days' | 'all';
export function Dashboard() {
  const [metrics, setMetrics] = useState<CTRMetrics>({
    totalVisits: 0,
    totalModalOpens: 0,
    totalSubmissions: 0,
    ctr: 0,
    modalOpenRate: 0,
    conversionRate: 0,
    uniqueVisits: 0,
    uniqueSubmissions: 0,
    uniqueCTR: 0
  });
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('all');
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    loadMetrics();
  }, [timePeriod, refreshKey]);
  const loadMetrics = () => {
    let startDate: Date | undefined;
    switch (timePeriod) {
      case 'today':
        startDate = new Date();
        startDate.setHours(0, 0, 0, 0);
        break;
      case '7days':
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 7);
        break;
      case '30days':
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);
        break;
      case 'all':
      default:
        startDate = undefined;
    }
    const calculatedMetrics = calculateCTR(startDate);
    setMetrics(calculatedMetrics);
  };
  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };
  const handleClearData = () => {
    if (window.confirm('¿Estás seguro de que quieres borrar todos los datos de analytics? Esta acción no se puede deshacer.')) {
      clearAnalytics();
      setRefreshKey(prev => prev + 1);
    }
  };
  const getRecentActivity = () => {
    const events = getAnalyticsEvents();
    return events.sort((a, b) => b.timestamp - a.timestamp).slice(0, 20).map(event => ({
      ...event,
      date: new Date(event.timestamp)
    }));
  };
  const recentActivity = getRecentActivity();
  return <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white" data-id="element-1232">
      {/* Header */}
      <header className="border-b border-gray-800 bg-gray-900 bg-opacity-80 backdrop-blur-lg" data-id="element-1233">
        <div className="container mx-auto px-4 py-6" data-id="element-1234">
          <div className="flex items-center justify-between" data-id="element-1235">
            <div data-id="element-1236">
              <h1 className="text-3xl font-bold text-white mb-2" data-id="element-1237">
                Analytics Dashboard
              </h1>
              <p className="text-gray-400" data-id="element-1238">
                Landing Page to Opt-in Conversion Metrics
              </p>
            </div>
            <div className="flex gap-3" data-id="element-1239">
              <button onClick={handleRefresh} className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors" data-id="element-1240">
                <RefreshCwIcon className="h-4 w-4" data-id="element-1241" />
                <span className="hidden sm:inline" data-id="element-1242">Refresh</span>
              </button>
              <button onClick={handleClearData} className="flex items-center gap-2 px-4 py-2 bg-red-900 bg-opacity-30 hover:bg-red-900 hover:bg-opacity-50 text-red-400 rounded-lg transition-colors" data-id="element-1243">
                <TrashIcon className="h-4 w-4" data-id="element-1244" />
                <span className="hidden sm:inline" data-id="element-1245">Clear Data</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8" data-id="element-1246">
        {/* Time Period Filter */}
        <div className="mb-8" data-id="element-1247">
          <div className="flex flex-wrap gap-2" data-id="element-1248">
            {[{
            value: 'today',
            label: 'Today'
          }, {
            value: '7days',
            label: 'Last 7 Days'
          }, {
            value: '30days',
            label: 'Last 30 Days'
          }, {
            value: 'all',
            label: 'All Time'
          }].map(period => <button key={period.value} onClick={() => setTimePeriod(period.value as TimePeriod)} className={`px-4 py-2 rounded-lg font-medium transition-all ${timePeriod === period.value ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-id="element-1249">
                {period.label}
              </button>)}
          </div>
        </div>

        {/* Primary Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-1250">
          {/* Page Visits */}
          <div className="bg-gray-800 bg-opacity-50 rounded-xl p-6 border border-gray-700" data-id="element-1251">
            <div className="flex items-center justify-between mb-4" data-id="element-1252">
              <div className="bg-purple-600 bg-opacity-20 rounded-lg p-3" data-id="element-1253">
                <MousePointerClickIcon className="h-6 w-6 text-purple-400" data-id="element-1254" />
              </div>
              <span className="text-sm text-gray-400" data-id="element-1255">Visits</span>
            </div>
            <div className="text-3xl font-bold text-white mb-1" data-id="element-1256">
              {metrics.totalVisits}
            </div>
            <div className="text-sm text-gray-400" data-id="element-1257">Landing Page Visits</div>
            <div className="mt-2 text-xs text-gray-500" data-id="element-1258">
              {metrics.uniqueVisits} unique sessions
            </div>
          </div>

          {/* Opt-ins */}
          <div className="bg-gray-800 bg-opacity-50 rounded-xl p-6 border border-gray-700" data-id="element-1259">
            <div className="flex items-center justify-between mb-4" data-id="element-1260">
              <div className="bg-green-600 bg-opacity-20 rounded-lg p-3" data-id="element-1261">
                <CheckCircle2Icon className="h-6 w-6 text-green-400" data-id="element-1262" />
              </div>
              <span className="text-sm text-gray-400" data-id="element-1263">Conversions</span>
            </div>
            <div className="text-3xl font-bold text-white mb-1" data-id="element-1264">
              {metrics.totalSubmissions}
            </div>
            <div className="text-sm text-gray-400" data-id="element-1265">Opt-ins</div>
            <div className="mt-2 text-xs text-gray-500" data-id="element-1266">
              {metrics.uniqueSubmissions} unique conversions
            </div>
          </div>

          {/* CTR */}
          <div className="bg-gray-800 bg-opacity-50 rounded-xl p-6 border border-gray-700" data-id="element-1267">
            <div className="flex items-center justify-between mb-4" data-id="element-1268">
              <div className="bg-red-600 bg-opacity-20 rounded-lg p-3" data-id="element-1269">
                <TrendingUpIcon className="h-6 w-6 text-red-400" data-id="element-1270" />
              </div>
              <span className="text-sm text-gray-400" data-id="element-1271">Rate</span>
            </div>
            <div className="text-3xl font-bold text-white mb-1" data-id="element-1272">
              {metrics.ctr.toFixed(2)}%
            </div>
            <div className="text-sm text-gray-400" data-id="element-1273">Conversion Rate</div>
            <div className="mt-2 text-xs text-gray-500" data-id="element-1274">
              {metrics.uniqueCTR.toFixed(2)}% unique CTR
            </div>
          </div>
        </div>

        {/* Secondary Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-1275">
          <div className="bg-gray-800 bg-opacity-50 rounded-xl p-6 border border-gray-700" data-id="element-1276">
            <div className="flex items-center justify-between mb-4" data-id="element-1277">
              <h3 className="text-lg font-semibold text-white" data-id="element-1278">
                Modal Open Rate
              </h3>
              <span className="text-2xl font-bold text-blue-400" data-id="element-1279">
                {metrics.modalOpenRate.toFixed(2)}%
              </span>
            </div>
            <p className="text-sm text-gray-400" data-id="element-1280">
              {metrics.totalModalOpens} of {metrics.totalVisits} visitors opened
              the modal
            </p>
          </div>

          <div className="bg-gray-800 bg-opacity-50 rounded-xl p-6 border border-gray-700" data-id="element-1281">
            <div className="flex items-center justify-between mb-4" data-id="element-1282">
              <h3 className="text-lg font-semibold text-white" data-id="element-1283">
                Modal Conversion
              </h3>
              <span className="text-2xl font-bold text-green-400" data-id="element-1284">
                {metrics.conversionRate.toFixed(2)}%
              </span>
            </div>
            <p className="text-sm text-gray-400" data-id="element-1285">
              {metrics.totalSubmissions} of {metrics.totalModalOpens} modal
              opens converted
            </p>
          </div>
        </div>

        {/* Conversion Funnel */}
        <div className="bg-gray-800 bg-opacity-50 rounded-xl p-6 border border-gray-700 mb-8" data-id="element-1286">
          <div className="flex items-center gap-2 mb-6" data-id="element-1287">
            <BarChart3Icon className="h-5 w-5 text-red-400" data-id="element-1288" />
            <h2 className="text-xl font-bold text-white" data-id="element-1289">Conversion Funnel</h2>
          </div>
          <div className="space-y-4" data-id="element-1290">
            <div data-id="element-1291">
              <div className="flex items-center justify-between mb-2" data-id="element-1292">
                <span className="text-sm text-gray-300" data-id="element-1293">
                  Landing Page Visits
                </span>
                <span className="text-sm font-semibold text-white" data-id="element-1294">
                  {metrics.totalVisits} (100%)
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-3" data-id="element-1295">
                <div className="bg-purple-500 h-3 rounded-full transition-all duration-500" style={{
                width: '100%'
              }} data-id="element-1296" />
              </div>
            </div>
            <div data-id="element-1297">
              <div className="flex items-center justify-between mb-2" data-id="element-1298">
                <span className="text-sm text-gray-300" data-id="element-1299">Modal Opened</span>
                <span className="text-sm font-semibold text-white" data-id="element-1300">
                  {metrics.totalModalOpens} ({metrics.modalOpenRate.toFixed(1)}
                  %)
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-3" data-id="element-1301">
                <div className="bg-blue-500 h-3 rounded-full transition-all duration-500" style={{
                width: `${metrics.modalOpenRate}%`
              }} data-id="element-1302" />
              </div>
            </div>
            <div data-id="element-1303">
              <div className="flex items-center justify-between mb-2" data-id="element-1304">
                <span className="text-sm text-gray-300" data-id="element-1305">Opted In</span>
                <span className="text-sm font-semibold text-white" data-id="element-1306">
                  {metrics.totalSubmissions} ({metrics.ctr.toFixed(1)}%)
                </span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-3" data-id="element-1307">
                <div className="bg-green-500 h-3 rounded-full transition-all duration-500" style={{
                width: `${metrics.ctr}%`
              }} data-id="element-1308" />
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-gray-800 bg-opacity-50 rounded-xl p-6 border border-gray-700" data-id="element-1309">
          <h2 className="text-xl font-bold text-white mb-6" data-id="element-1310">Recent Activity</h2>
          {recentActivity.length === 0 ? <div className="text-center py-12 text-gray-400" data-id="element-1311">
              <BarChart3Icon className="h-12 w-12 mx-auto mb-4 opacity-50" data-id="element-1312" />
              <p data-id="element-1313">No activity recorded yet</p>
              <p className="text-sm mt-2" data-id="element-1314">
                Data will appear here once users visit the landing page
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-1315">
              <table className="w-full" data-id="element-1316">
                <thead data-id="element-1317">
                  <tr className="border-b border-gray-700" data-id="element-1318">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300" data-id="element-1319">
                      Event
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300" data-id="element-1320">
                      Date & Time
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300" data-id="element-1321">
                      Session ID
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-1322">
                  {recentActivity.map((event, index) => <tr key={index} className="border-b border-gray-800 hover:bg-gray-700 hover:bg-opacity-30 transition-colors" data-id="element-1323">
                      <td className="py-3 px-4" data-id="element-1324">
                        <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${event.type === 'page_visit' ? 'bg-purple-600 bg-opacity-20 text-purple-400' : event.type === 'modal_opened' ? 'bg-blue-600 bg-opacity-20 text-blue-400' : 'bg-green-600 bg-opacity-20 text-green-400'}`} data-id="element-1325">
                          {event.type === 'page_visit' ? <MousePointerClickIcon className="h-3 w-3" data-id="element-1326" /> : event.type === 'modal_opened' ? <MousePointerClickIcon className="h-3 w-3" data-id="element-1327" /> : <CheckCircle2Icon className="h-3 w-3" data-id="element-1328" />}
                          {event.type === 'page_visit' ? 'Page Visit' : event.type === 'modal_opened' ? 'Modal Opened' : 'Opted In'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-300" data-id="element-1329">
                        {event.date.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-xs text-gray-500 font-mono" data-id="element-1330">
                        {event.sessionId.substring(0, 20)}...
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>
      </main>
    </div>;
}